"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_MASTER_DATA_PERMISSIONS = exports.MasterDataPermissions = void 0;
/**
 * Permission string catalog for the Master Data Platform (Stage 3).
 * Format: `<module>:<action>` — same convention as
 * platform.permissions.ts. Every entity gets `read`/`create`/`update`/
 * `delete`; administrative bulk actions (activate, deactivate, bulk
 * import, bulk export) collapse into one `manage` permission per entity
 * rather than one permission per micro-action, mirroring Stage 2's
 * `license:manage`/`sequence:manage`/`working-hours:manage` pattern.
 * Viewing an entity's change history is gated by its `read` permission,
 * not `manage` — inspecting history isn't a mutating action.
 *
 * Country/State/City share one `geography:*` permission set rather than
 * three, since they're administered as a single "Geography" screen in
 * practice (a hospital doesn't grant "can edit States" separately from
 * "can edit Cities").
 */
exports.MasterDataPermissions = {
    LOOKUP_TYPE_READ: 'lookup-type:read',
    LOOKUP_TYPE_CREATE: 'lookup-type:create',
    LOOKUP_TYPE_UPDATE: 'lookup-type:update',
    LOOKUP_TYPE_DELETE: 'lookup-type:delete',
    LOOKUP_TYPE_MANAGE: 'lookup-type:manage',
    LOOKUP_VALUE_READ: 'lookup-value:read',
    LOOKUP_VALUE_CREATE: 'lookup-value:create',
    LOOKUP_VALUE_UPDATE: 'lookup-value:update',
    LOOKUP_VALUE_DELETE: 'lookup-value:delete',
    LOOKUP_VALUE_MANAGE: 'lookup-value:manage',
    GEOGRAPHY_READ: 'geography:read',
    GEOGRAPHY_CREATE: 'geography:create',
    GEOGRAPHY_UPDATE: 'geography:update',
    GEOGRAPHY_DELETE: 'geography:delete',
    GEOGRAPHY_MANAGE: 'geography:manage',
    // Registration redesign — Area/Locality gets its OWN permission set
    // (not folded into GEOGRAPHY_*) specifically so front-desk/registration
    // roles can be granted just `AREA_CREATE` (inline "+ Add New Area" from
    // the registration screen) without also getting full Country/State/City
    // management rights.
    AREA_READ: 'area:read',
    AREA_CREATE: 'area:create',
    AREA_UPDATE: 'area:update',
    AREA_DELETE: 'area:delete',
    AREA_MANAGE: 'area:manage',
    DIAGNOSIS_READ: 'diagnosis:read',
    DIAGNOSIS_CREATE: 'diagnosis:create',
    DIAGNOSIS_UPDATE: 'diagnosis:update',
    DIAGNOSIS_DELETE: 'diagnosis:delete',
    DIAGNOSIS_MANAGE: 'diagnosis:manage',
    PROCEDURE_READ: 'procedure:read',
    PROCEDURE_CREATE: 'procedure:create',
    PROCEDURE_UPDATE: 'procedure:update',
    PROCEDURE_DELETE: 'procedure:delete',
    PROCEDURE_MANAGE: 'procedure:manage',
    TEST_MASTER_READ: 'test-master:read',
    TEST_MASTER_CREATE: 'test-master:create',
    TEST_MASTER_UPDATE: 'test-master:update',
    TEST_MASTER_DELETE: 'test-master:delete',
    TEST_MASTER_MANAGE: 'test-master:manage',
    // Admin Override (2026-09-22), same shape as
    // `PHARMACY_SALE_DISCOUNT_EDIT_POSTED`: classification/specimen fields
    // otherwise lock once a test leaves DRAFT (see
    // `TestMasterService.assertDraftOnlyFieldsAllowed`); only a caller holding
    // this permission may amend them directly, and only with a stated reason.
    TEST_MASTER_ADMIN_OVERRIDE: 'test-master:admin-override',
    RADIOLOGY_PROCEDURE_READ: 'radiology-procedure:read',
    RADIOLOGY_PROCEDURE_CREATE: 'radiology-procedure:create',
    RADIOLOGY_PROCEDURE_UPDATE: 'radiology-procedure:update',
    RADIOLOGY_PROCEDURE_DELETE: 'radiology-procedure:delete',
    RADIOLOGY_PROCEDURE_MANAGE: 'radiology-procedure:manage',
    DRUG_MASTER_READ: 'drug-master:read',
    DRUG_MASTER_CREATE: 'drug-master:create',
    DRUG_MASTER_UPDATE: 'drug-master:update',
    DRUG_MASTER_DELETE: 'drug-master:delete',
    DRUG_MASTER_MANAGE: 'drug-master:manage',
    // Stage 14, Phase 1 (Pharmacy Master Data Foundation) — approval
    // governance is new for master data (ADR-030 decision 11); reuses this
    // existing catalog entry rather than a colliding new "DrugMasterPermissions".
    DRUG_MASTER_APPROVE: 'drug-master:approve',
    ITEM_MASTER_READ: 'item-master:read',
    ITEM_MASTER_CREATE: 'item-master:create',
    ITEM_MASTER_UPDATE: 'item-master:update',
    ITEM_MASTER_DELETE: 'item-master:delete',
    ITEM_MASTER_MANAGE: 'item-master:manage',
    CHARGE_MASTER_READ: 'charge-master:read',
    CHARGE_MASTER_CREATE: 'charge-master:create',
    CHARGE_MASTER_UPDATE: 'charge-master:update',
    CHARGE_MASTER_DELETE: 'charge-master:delete',
    CHARGE_MASTER_MANAGE: 'charge-master:manage',
    PAYER_MASTER_READ: 'payer-master:read',
    PAYER_MASTER_CREATE: 'payer-master:create',
    PAYER_MASTER_UPDATE: 'payer-master:update',
    PAYER_MASTER_DELETE: 'payer-master:delete',
    PAYER_MASTER_MANAGE: 'payer-master:manage',
    // Stage 13, Milestone 2 (Part 2) — Accounts Payable. VendorMaster
    // mirrors PayerMaster's own placement/permission shape exactly.
    VENDOR_MASTER_READ: 'vendor-master:read',
    VENDOR_MASTER_CREATE: 'vendor-master:create',
    VENDOR_MASTER_UPDATE: 'vendor-master:update',
    VENDOR_MASTER_DELETE: 'vendor-master:delete',
    VENDOR_MASTER_MANAGE: 'vendor-master:manage',
    // Corporate Credit Management (Phase 1 — master data only). Same
    // READ/CREATE/UPDATE/DELETE/MANAGE shape as VENDOR_MASTER_* above.
    CORPORATE_READ: 'corporate:read',
    CORPORATE_CREATE: 'corporate:create',
    CORPORATE_UPDATE: 'corporate:update',
    CORPORATE_DELETE: 'corporate:delete',
    CORPORATE_MANAGE: 'corporate:manage',
    CORPORATE_AGREEMENT_READ: 'corporate-agreement:read',
    CORPORATE_AGREEMENT_CREATE: 'corporate-agreement:create',
    CORPORATE_AGREEMENT_UPDATE: 'corporate-agreement:update',
    CORPORATE_AGREEMENT_DELETE: 'corporate-agreement:delete',
    CORPORATE_AGREEMENT_MANAGE: 'corporate-agreement:manage',
    CORPORATE_CONTACT_READ: 'corporate-contact:read',
    CORPORATE_CONTACT_CREATE: 'corporate-contact:create',
    CORPORATE_CONTACT_UPDATE: 'corporate-contact:update',
    CORPORATE_CONTACT_DELETE: 'corporate-contact:delete',
    CORPORATE_CONTACT_MANAGE: 'corporate-contact:manage',
    CORPORATE_CREDIT_POLICY_READ: 'corporate-credit-policy:read',
    CORPORATE_CREDIT_POLICY_CREATE: 'corporate-credit-policy:create',
    CORPORATE_CREDIT_POLICY_UPDATE: 'corporate-credit-policy:update',
    CORPORATE_CREDIT_POLICY_DELETE: 'corporate-credit-policy:delete',
    CORPORATE_CREDIT_POLICY_MANAGE: 'corporate-credit-policy:manage',
    // Enterprise Billing Workstation — Payment Mode: Employee Credit.
    // Mirrors PayerMaster's own placement/permission shape exactly.
    // Employee Master enterprise rebuild — IMPORT/EXPORT/LOGIN_MANAGE
    // split out as their own named permissions per the spec's explicit
    // security list; MANAGE is kept for activate/deactivate only.
    EMPLOYEE_MASTER_READ: 'employee-master:read',
    EMPLOYEE_MASTER_CREATE: 'employee-master:create',
    EMPLOYEE_MASTER_UPDATE: 'employee-master:update',
    EMPLOYEE_MASTER_DELETE: 'employee-master:delete',
    EMPLOYEE_MASTER_MANAGE: 'employee-master:manage',
    EMPLOYEE_MASTER_IMPORT: 'employee-master:import',
    EMPLOYEE_MASTER_EXPORT: 'employee-master:export',
    EMPLOYEE_MASTER_LOGIN_MANAGE: 'employee-master:login-manage',
    // Employee Master enterprise rebuild — Designation catalog.
    DESIGNATION_READ: 'designation:read',
    DESIGNATION_CREATE: 'designation:create',
    DESIGNATION_UPDATE: 'designation:update',
    DESIGNATION_DELETE: 'designation:delete',
    // Emp List Register (2026-10-15) - the people entitled to the staff concession / credit facility.
    // Activate / deactivate ride on UPDATE, exactly as Designation does.
    EMP_LIST_READ: 'emp-list:read',
    EMP_LIST_CREATE: 'emp-list:create',
    EMP_LIST_UPDATE: 'emp-list:update',
    EMP_LIST_DELETE: 'emp-list:delete',
    EMP_LIST_IMPORT: 'emp-list:import',
    EMP_LIST_EXPORT: 'emp-list:export',
    // Doctor Master enterprise rebuild — same IMPORT/EXPORT split as
    // Employee Master above (MANAGE is activate/deactivate only).
    DOCTOR_MASTER_READ: 'doctor-master:read',
    DOCTOR_MASTER_CREATE: 'doctor-master:create',
    DOCTOR_MASTER_UPDATE: 'doctor-master:update',
    DOCTOR_MASTER_DELETE: 'doctor-master:delete',
    DOCTOR_MASTER_MANAGE: 'doctor-master:manage',
    DOCTOR_MASTER_IMPORT: 'doctor-master:import',
    DOCTOR_MASTER_EXPORT: 'doctor-master:export',
    // Hospital Infrastructure — Ward -> Nursing Station -> Room -> Bed.
    // Same READ/CREATE/UPDATE/DELETE/MANAGE shape as AREA_* above (MANAGE
    // covers activate/deactivate/import/export).
    WARD_READ: 'ward:read',
    WARD_CREATE: 'ward:create',
    WARD_UPDATE: 'ward:update',
    WARD_DELETE: 'ward:delete',
    WARD_MANAGE: 'ward:manage',
    NURSING_STATION_READ: 'nursing-station:read',
    NURSING_STATION_CREATE: 'nursing-station:create',
    NURSING_STATION_UPDATE: 'nursing-station:update',
    NURSING_STATION_DELETE: 'nursing-station:delete',
    NURSING_STATION_MANAGE: 'nursing-station:manage',
    ROOM_READ: 'room:read',
    ROOM_CREATE: 'room:create',
    ROOM_UPDATE: 'room:update',
    ROOM_DELETE: 'room:delete',
    ROOM_MANAGE: 'room:manage',
    BED_READ: 'bed:read',
    BED_CREATE: 'bed:create',
    BED_UPDATE: 'bed:update',
    BED_DELETE: 'bed:delete',
    BED_MANAGE: 'bed:manage',
    MESSAGE_TEMPLATE_READ: 'message-template:read',
    MESSAGE_TEMPLATE_CREATE: 'message-template:create',
    MESSAGE_TEMPLATE_UPDATE: 'message-template:update',
    MESSAGE_TEMPLATE_DELETE: 'message-template:delete',
    MESSAGE_TEMPLATE_MANAGE: 'message-template:manage',
};
/** Every master-data permission, flattened — used to seed the `permissions` table. */
exports.ALL_MASTER_DATA_PERMISSIONS = Object.values(exports.MasterDataPermissions);
//# sourceMappingURL=master-data.permissions.js.map